import {
  ChatsByPhoneAndDate,
  MessageLog,
  MessageResolutionStatus,
  TrackingNumber,
  TrackingNumberWithAgents,
  TwilioMessageStatus,
} from '../../../interfaces/appointments';

export const resolveMessageLabel = (
  message: MessageLog,
  systemPhone?: TrackingNumberWithAgents | null,
) => {
  if (
    !!message.coreProviderId &&
    !!systemPhone &&
    !!systemPhone.label &&
    (systemPhone.agents || []).some((a) => a.coreProviderId === message.coreProviderId)
  ) {
    return systemPhone.label;
  }

  return message.coreProviderFullname || systemPhone?.label || 'Unknown';
};

export const simplifiedResolutionStatus = (status?: string) => {
  switch (status) {
    case MessageResolutionStatus.AutomaticallyResolved:
      return 'system';
    case MessageResolutionStatus.ManuallyResolved:
      return 'agent';
    case MessageResolutionStatus.Pending:
      return 'pending';
    default:
      return 'unknown';
  }
};

export const simplifiedResolutionStatusColor = (status?: string) => {
  switch (status) {
    case MessageResolutionStatus.AutomaticallyResolved:
      return 'default';
    case MessageResolutionStatus.ManuallyResolved:
      return 'success';
    case MessageResolutionStatus.Pending:
      return 'warning';
    default:
      return 'error';
  }
};

export const simplifiedDeliveryStatusColor = (status?: string) => {
  switch (status) {
    case TwilioMessageStatus.Sent:
    case TwilioMessageStatus.Delivered:
      return 'success';
    case TwilioMessageStatus.Failed:
    case TwilioMessageStatus.FailedIntent:
    case TwilioMessageStatus.Undelivered:
    case TwilioMessageStatus.Canceled:
      return 'error';
    default:
      return 'default';
  }
};

export const mergeChatsByPhone = (
  trackingNumbers: TrackingNumber[],
  backwardMessages: ChatsByPhoneAndDate,
  forwardMessages: ChatsByPhoneAndDate,
): ChatsByPhoneAndDate => {
  const result: ChatsByPhoneAndDate = {};

  // Get all phones who sent messages to member
  const allPhones = Array.from(
    new Set([
      ...trackingNumbers.map((t) => t.trackingNumber),
      ...Object.keys(backwardMessages),
      ...Object.keys(forwardMessages),
    ]),
  );

  for (const phone of allPhones) {
    const phoneResult: { [date: string]: MessageLog[] } = {};
    const phoneBackwardMessages = backwardMessages[phone] || {};
    const phoneForwardMessages = forwardMessages[phone] || {};

    // Get all dates where messages were sent
    const allDates = Array.from(
      new Set([...Object.keys(phoneBackwardMessages), ...Object.keys(phoneForwardMessages)]),
    );

    // Merge messages by date
    for (const date of allDates) {
      const uniqueMessages = new Map<number, MessageLog>();

      if (phoneBackwardMessages[date]) {
        for (const msg of phoneBackwardMessages[date]) {
          uniqueMessages.set(msg.id, msg);
        }
      }

      if (phoneForwardMessages[date]) {
        for (const msg of phoneForwardMessages[date]) {
          uniqueMessages.set(msg.id, msg);
        }
      }

      phoneResult[date] = Array.from(uniqueMessages.values());
    }

    result[phone] = phoneResult;
  }

  return result;
};
